import { useEffect, useMemo, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrAnlageDetail, MsrAnlageZeile } from './typen';
import { GEWERK, herkunftMarke } from './gemeinsam';
import {
  ContentSection, EmptyState, ErrorState, Feld, LoadingState, StatusBadge,
} from '../../muster';
import { Funktionsbeschreibung } from './Funktionsbeschreibung';
import { Einstellwerte } from './Einstellwerte';
import { RANG_SEITE, msrZiele, useMsrExport } from './kopf';

const KATEGORIE_TEXT: Record<string, string> = {
  eingabe_ausgabe: 'Eingabe/Ausgabe', verarbeitung: 'Verarbeitung',
  bedienen_beobachten: 'Bedienen/Beobachten', management: 'Management',
};
const fehlertext = (e: unknown) => (e instanceof Error ? e.message : String(e));

const KATEGORIE_REIHENFOLGE = [
  'eingabe_ausgabe', 'verarbeitung', 'bedienen_beobachten', 'management',
];

/**
 * Reiter „GA-Funktionen" — Auswahl Anlage links, rechts die Funktionsmatrix
 * (Kategorie × Funktionen) mit Herkunftsmarke, Funktionsbeschreibung wie im
 * Baum, Sammelknopf für alle Anlagen ohne Beschreibung. Die Excel-Funktionsliste
 * steht seit TM-8a in der Exportleiste im Kopf (`useMsrExport`).
 */
export function Funktionen({ api, projektId, anlagen }: {
  api: ApiClient; projektId: string; anlagen: MsrAnlageZeile[];
}) {
  const [suche, setSuche] = useState('');
  const [gewaehltId, setGewaehltId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MsrAnlageDetail | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  // TM-8c: Fehler der Erzeugungen getrennt vom Ladefehler der Anlage - vorher hieß jeder „konnte nicht geladen werden“.
  const [aktionFehler, setAktionFehler] = useState<{ titel: string; text: string } | null>(null);
  const [erzeugtLaeuft, setErzeugtLaeuft] = useState(false);
  const [sammelLaeuft, setSammelLaeuft] = useState(false);
  const [sammelErgebnis, setSammelErgebnis] = useState<string | null>(null);
  const [einstellwerteSammelLaeuft, setEinstellwerteSammelLaeuft] = useState(false);
  const [einstellwerteSammelErgebnis, setEinstellwerteSammelErgebnis] = useState<string | null>(null);
  useMsrExport(RANG_SEITE, () => [msrZiele.funktionsliste(api, projektId)], [api, projektId]);

  const gefiltert = useMemo(() => anlagen.filter((a) =>
    !suche.trim()
    || a.kennung.toLowerCase().includes(suche.trim().toLowerCase())
    || (a.bezeichnung ?? '').toLowerCase().includes(suche.trim().toLowerCase())),
  [anlagen, suche]);

  const detailNeuLaden = (id: string) => {
    setFehler(null);
    api.msrAnlage(projektId, id).then(setDetail)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    if (gewaehltId) detailNeuLaden(gewaehltId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gewaehltId]);

  const beschreibungErzeugen = async (force: boolean) => {
    if (!gewaehltId) return;
    setErzeugtLaeuft(true);
    setAktionFehler(null);
    try {
      await api.msrBeschreibungErzeugen(projektId, gewaehltId, force);
      detailNeuLaden(gewaehltId);
    } catch (e) {
      setAktionFehler({ titel: 'Die Funktionsbeschreibung konnte nicht erzeugt werden.', text: fehlertext(e) });
    } finally {
      setErzeugtLaeuft(false);
    }
  };

  const sammelErzeugen = async () => {
    setSammelLaeuft(true);
    setSammelErgebnis(null);
    setAktionFehler(null);
    try {
      const r = await api.msrBeschreibungenErzeugen(projektId, true);
      setSammelErgebnis(
        `${r.erzeugt} erzeugt, ${r.uebersprungen} übersprungen, ${r.fehler} Fehler `
        + `(${r.eingabeTokens}/${r.ausgabeTokens} Token).`);
    } catch (e) {
      setAktionFehler({ titel: 'Die Beschreibungen konnten nicht erzeugt werden.', text: fehlertext(e) });
    } finally {
      setSammelLaeuft(false);
    }
  };

  const einstellwerteSammelErzeugen = async () => {
    setEinstellwerteSammelLaeuft(true);
    setEinstellwerteSammelErgebnis(null);
    setAktionFehler(null);
    try {
      const r = await api.msrEinstellwerteVorschlagenAlle(projektId, true);
      setEinstellwerteSammelErgebnis(
        `${r.erzeugt} erzeugt, ${r.geaendert} geändert, ${r.beibehalten} beibehalten `
        + `(${r.eingabeTokens}/${r.ausgabeTokens} Token, ${r.modell}).`);
    } catch (e) {
      setAktionFehler({ titel: 'Die Einstellwert-Vorschläge konnten nicht erzeugt werden.', text: fehlertext(e) });
    } finally {
      setEinstellwerteSammelLaeuft(false);
    }
  };


  const nachKategorie = detail
    ? KATEGORIE_REIHENFOLGE.map((k) => ({
        kategorie: k,
        funktionen: detail.funktionen.filter((f) => f.kategorie === k),
      })).filter((g) => g.funktionen.length > 0)
    : [];

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div className="baum" style={{ minWidth: 260, maxWidth: 320 }}>
        <div style={{ padding: 8, borderBottom: '1px solid var(--linie)' }}>
          <Feld art="suche" beschriftung="Anlage suchen" wert={suche}
            platzhalter="Kennung oder Bezeichnung …"
            beiAenderung={(w) => setSuche(w as string)} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
            <button type="button" className="m-knopf" disabled={sammelLaeuft}
              onClick={sammelErzeugen}
              style={{ fontSize: 11, whiteSpace: 'normal', textAlign: 'left', height: 'auto' }}>
              {sammelLaeuft ? 'läuft …'
                : 'Beschreibungen für alle ohne Beschreibung erzeugen'}
            </button>
            <button type="button" className="m-knopf" disabled={einstellwerteSammelLaeuft}
              onClick={einstellwerteSammelErzeugen}
              style={{ fontSize: 11, whiteSpace: 'normal', textAlign: 'left', height: 'auto' }}>
              {einstellwerteSammelLaeuft ? 'läuft …'
                : 'Einstellwert-Vorschläge für alle Anlagen ohne KI-Vorschlag erzeugen'}
            </button>
          </div>
          {sammelErgebnis && (
            <p className="schwach" role="status" aria-live="polite" style={{ marginTop: 6, fontSize: 11 }}>
              {sammelErgebnis}
            </p>
          )}
          {einstellwerteSammelErgebnis && (
            <p className="schwach" role="status" aria-live="polite" style={{ marginTop: 6, fontSize: 11 }}>
              {einstellwerteSammelErgebnis}
            </p>
          )}
        </div>

        {gefiltert.map((a) => (
          <button key={a.id} type="button"
            className={`baum-zeile${gewaehltId === a.id ? ' aktiv' : ''}`}
            style={{ width: '100%' }} onClick={() => setGewaehltId(a.id)}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {a.kennung}
            </span>
            <span className="schwach" style={{ fontSize: 10 }}>{GEWERK[a.gewerk]}</span>
            <span className="schwach">{a.funktionen}</span>
          </button>
        ))}
        {gefiltert.length === 0 && (
          <EmptyState
            klein
            titel={anlagen.length === 0
              ? 'Noch keine Anlage abgeleitet.'
              : 'Keine Anlage trägt diesen Suchbegriff.'}
            erklaerung={anlagen.length === 0
              ? 'Anlagen entstehen aus dem Ableitungslauf über die Gewerkemodelle.'
              : `${anlagen.length} Anlagen sind vorhanden.`}
            aktion={anlagen.length > 0 ? (
              <button type="button" className="m-knopf" onClick={() => setSuche('')}>
                Suche leeren
              </button>
            ) : undefined}
          />
        )}
      </div>

      <div style={{ flex: '1 1 640px', minWidth: 320 }}>
        {aktionFehler && <ErrorState klein titel={aktionFehler.titel} technisch={aktionFehler.text} />}

        {fehler && (
          <ErrorState
            meldung="Die GA-Funktionen dieser Anlage konnten nicht geladen werden."
            technisch={fehler}
            beiErneutVersuchen={() => gewaehltId && detailNeuLaden(gewaehltId)}
          />
        )}

        {!gewaehltId && (
          <EmptyState
            titel="Keine Anlage gewählt."
            erklaerung={'Links eine Anlage wählen — rechts erscheinen ihre GA-Funktionen '
              + 'nach VDI 3814, die Einstellwerte und die Funktionsbeschreibung.'}
          />
        )}

        {gewaehltId && !detail && !fehler && (
          <LoadingState text="Anlage wird abgerufen …" zeilen={6} spalten={2} />
        )}

        {detail && (
          <>
            <ContentSection titel={detail.kennung} beschreibung={detail.bezeichnung ?? undefined}>
              {nachKategorie.map((g) => (
                <div key={g.kategorie} style={{ marginBottom: 10 }}>
                  <div className="feldname">{KATEGORIE_TEXT[g.kategorie] ?? g.kategorie}</div>
                  <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
                    {g.funktionen.map((f) => (
                      <li key={f.id} style={{ fontSize: 13 }}>
                        {f.funktion}{' '}
                        <StatusBadge {...herkunftMarke(f.herkunft)} klein />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {nachKategorie.length === 0 && (
                <EmptyState
                  klein
                  titel="Für diese Anlage ist keine GA-Funktion abgeleitet."
                  erklaerung={'GA-Funktionen entstehen aus den Funktionsvorlagen je '
                    + 'Aggregat — ohne Aggregat gibt es keine Funktion.'}
                />
              )}
            </ContentSection>

            <Einstellwerte api={api} projektId={projektId} anlageId={detail.id} />

            <Funktionsbeschreibung
              beschreibung={detail.beschreibung}
              onErzeugen={beschreibungErzeugen}
              erzeugtLaeuft={erzeugtLaeuft}
            />
          </>
        )}
      </div>
    </div>
  );
}
